import React from 'react';
import { Helmet } from 'react-helmet-async';

export const SITE_URL = 'https://newaudia.com';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-default.png`;

export const PAGE_SEO = {
  home: {
    path: '/',
    title: 'Interactive Reading with Audia | NewAudia',
    description: "NewAudia is an interactive reading platform: read, listen, and ask. Audia, your AI reading companion, answers questions grounded in each book's text.",
  },
  ai: {
    path: '/ai',
    title: 'What Is NewAudia? Read. Listen. Ask. | NewAudia',
    description: 'NewAudia is an interactive reading platform designed to make books more usable: read or listen, then ask Audia, the AI reading companion, about the book.',
  },
  howItWorks: {
    path: '/how-it-works',
    title: 'How It Works | NewAudia',
    description: "NewAudia is built around a simple idea: books should be usable. Read, listen, move through chapters, and ask Audia questions about the book you're in.",
  },
  whyNewAudia: {
    path: '/why-newaudia',
    title: 'Why NewAudia: Beyond Linear Listening | NewAudia',
    description: "Audiobooks made books more accessible, but accessibility alone isn't enough. NewAudia adds structure and interaction: read, listen, and ask Audia.",
  },
  forAuthors: {
    path: '/for-authors',
    title: 'For Authors | NewAudia',
    description: 'NewAudia is for authors who believe books should do more. Publish an interactive edition readers can read, listen to, and question with Audia.',
  },
  forPublishers: {
    path: '/for-publishers',
    title: 'For Publishers | NewAudia',
    description: 'A publishing platform for interactive books: distribute titles readers can read, listen to, and ask questions about with Audia, the AI reading companion.',
  },
  library: {
    path: '/public/library',
    title: 'Public Library | NewAudia',
    description: 'Browse the NewAudia Public Library. Discover books designed for reading, listening, and deep engagement, then tap any title to learn more or hear a sample.',
  },
  authors: {
    path: '/authors',
    title: 'Our Authors | NewAudia',
    description: "Meet the authors whose books you can read, listen to, and talk to on NewAudia. Explore each author's profile and browse their titles in the library.",
  },
  pricing: {
    path: '/pricing',
    title: 'Pricing & Reader Credits | NewAudia',
    description: 'Stories should pull you in, not make you buy the whole book. With NewAudia you pay for engagement, not ownership, using Reader Credits or a plan.',
  },
  about: {
    path: '/about',
    title: 'About | NewAudia',
    description: 'NewAudia turns manuscripts into interactive reading experiences: read, listen, and ask. Learn about the platform and Audia, your AI reading companion.',
  },
  faq: {
    path: '/faq',
    title: 'FAQ | NewAudia',
    description: 'Answers to common questions about NewAudia, the NFD-SmartBook format, and how the platform works for readers and authors during early access.',
  },
  contact: {
    path: '/contact',
    title: 'Contact | NewAudia',
    description: "Contact NewAudia. Whether you're an author, reader, partner, or investor, we'd love to hear from you. Reach out anytime with questions or ideas.",
  },
  terms: {
    path: '/terms',
    title: 'Terms of Service | NewAudia',
    description: 'The Terms of Service that govern access to and use of the NewAudia website, applications, and services, provided by NewAudia Systems, LLC.',
  },
  privacy: {
    path: '/privacy',
    title: 'Privacy Policy | NewAudia',
    description: 'How NewAudia Systems, LLC collects, uses, discloses, protects, and retains personal information when you use the NewAudia website, apps, and services.',
  },
  acceptableUse: {
    path: '/acceptable-use',
    title: 'Acceptable Use Policy | NewAudia',
    description: 'The Acceptable Use Policy that governs access to and use of the NewAudia platform, website, applications, and services, and applies to all users.',
  },
  aiPolicy: {
    path: '/ai-policy',
    title: 'AI Usage Policy | NewAudia',
    description: 'The AI Usage Policy that governs AI-assisted features within the NewAudia platform, website, applications, and services. Effective March 1, 2026.',
  },
  addendum: {
    path: '/author-publisher-addendum',
    title: 'Author & Publisher Addendum | NewAudia',
    description: 'The Author & Publisher Addendum supplements the NewAudia Author Publishing Agreement with additional terms for authors and publishers on NewAudia.',
  },
  readerCredits: {
    path: '/reader-credits-billing',
    title: 'Reader Credit & Billing Policy | NewAudia',
    description: 'How Reader Credits are acquired, consumed, billed, and expire on NewAudia. This policy governs Reader Credits across the platform, website, and apps.',
  },
};

// Shared Helmet wrapper: title, description, canonical, og:*, twitter:*, optional JSON-LD and noindex.
export const Seo = ({ title, description, path, canonicalUrl, image = DEFAULT_OG_IMAGE, type = 'website', jsonLd, noindex = false }) => {
  const url = canonicalUrl || (path ? `${SITE_URL}${path}` : null);
  return (
    <Helmet>
      <title>{title}</title>
      {description && <meta name="description" content={description} />}
      {url && <link rel="canonical" href={url} />}
      {noindex && <meta name="robots" content="noindex" />}
      <meta property="og:site_name" content="NewAudia" />
      <meta property="og:title" content={title} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:type" content={type} />
      {url && <meta property="og:url" content={url} />}
      {image && <meta property="og:image" content={image} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      {description && <meta name="twitter:description" content={description} />}
      {image && <meta name="twitter:image" content={image} />}
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
};

export default Seo;
